import {USER_KEYS} from "@/api/userApi";
import {client} from "@/httpClient";
import {useCategoryStore} from "@/store/category";
import {create} from "@/store/createStore";
import useTokenStore from "@/store/token";
import type {TLanguage, TLearnAboutUsOption, TNonFunctionKeys, TSubscription, TUserState} from "@/store/types";
import urlify from "@/utils/urlify";
import type {AxiosResponse} from "axios";
import qs from "qs";
import {z} from "zod";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

const AddressSchema = z.object({
  addressStreet: z
    .string({message: "company_profile:missing_address_street_error"})
    .min(1, "company_profile:missing_address_street_error")
    .superRefine((val, ctx) => {
      if (val.length && !/\d/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "company_profile:missing_address_street_number_error",
        });
      }
    }),
  addressPostal: z
    .string({message: "company_profile:missing_address_postal_error"})
    .min(1, "company_profile:missing_address_postal_error"),
  addressCity: z
    .string({message: "company_profile:missing_address_city_error"})
    .min(1, "company_profile:missing_address_city_error"),
});

const CredentialsSchema = z.object({
  email: z.string().email("invalidEmail"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Surname is required"),
  password: z.string().min(8, "passwordAtLeast8Charaters"),
  isEmailValidated: z.boolean().refine(value => value),
});

const CompanyDetailsSchema = z
  .object({
    companyName: z.string().min(1, "company_profile:missing_company_name_error"),
    companyWebsite: z.string().url("company_profile:missing_website_error"),
    phoneNumber: z.string().min(1, "incorrect_phone_number_length"),
  })
  .extend(AddressSchema.shape);

const InterestSelectionSchema = z.object({
  companyInstagramUsername: z.string().min(1, "Username is required"),
  companyTikTokUsername: z.string().optional(),
});

export const EmailSchema = z.object({
  email: z.string().email("invalidEmail"),
});

type TSchema<T> = T extends typeof CredentialsSchema
  ? typeof CredentialsSchema
  : T extends typeof CompanyDetailsSchema
    ? typeof CompanyDetailsSchema
    : T extends typeof InterestSelectionSchema
      ? typeof InterestSelectionSchema
      : T extends typeof EmailSchema
        ? typeof EmailSchema
        : T extends typeof AddressSchema
          ? typeof AddressSchema
          : never;

export type TRegistrationState = {
  addressCity: string | null;
  addressPostal: string | null;
  addressStreet: string | null;
  country: string | null;
  step: "credentials" | "company_details" | "interest_selection";
  isRegisteringAfterDirectPurchase?: boolean;
  isEmailPrefilled?: boolean;
  companyId?: number;
  companyLogo?: string | null;
  userId?: number;
  package_subscription?: TSubscription | null;
  languageCode?: TLanguage;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  companyName: string;
  companyWebsite: string;
  phoneNumber: string;
  placeId?: string | null;
  placeDescription?: string | null;
  companyInstagramUsername: string;
  companyTikTokUsername?: string;
  companyBio: string;
  selectedLearnAboutUs: TLearnAboutUsOption | null;
  learnAboutUsExtra: string;
  interests: number[];
  isEmailValidated: boolean;
  isInstagramValidated: boolean;
  isTikTokValidated: boolean;
  stepValidations: {
    [key in TRegistrationState["step"]]: boolean;
  };
  errors: {
    [K in TNonFunctionKeys<
      Omit<
        TRegistrationState,
        | "errors"
        | "step"
        | "isRegisteringAfterDirectPurchase"
        | "isEmailPrefilled"
        | "companyId"
        | "companyLogo"
        | "userId"
        | "languageCode"
        | "package_subscription"
        | "interests"
        | "isEmailValidated"
        | "stepValidations"
        | "isInstagramValidated"
        | "isTikTokValidated"
      >
    >]: string;
  };
  selectInterest(interest: number): void;
  reset(): void;
  setValue<K extends TNonFunctionKeys<TRegistrationState>>(key: K, value: TRegistrationState[K]): void;
  setError<K extends TNonFunctionKeys<TRegistrationState["errors"]>>(
    key: K,
    value: TRegistrationState["errors"][K],
  ): void;
  goToNextPage: () => void;
  register(): Promise<AxiosResponse<{user: TUserState}>>;
  validateAddress(): boolean;
  validateSchema<
    T extends
      | typeof CredentialsSchema
      | typeof CompanyDetailsSchema
      | typeof InterestSelectionSchema
      | typeof EmailSchema
      | typeof AddressSchema,
  >(
    schema: TSchema<T>,
    step?: TRegistrationState["step"],
  ): boolean;
  validateCredentials(): boolean;
  validateCompanyDetails(): boolean;
  validateInterestSelection(): boolean;
};

const initialState = {
  step: "credentials",
  isRegisteringAfterDirectPurchase: false,
  isEmailPrefilled: false,
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  companyName: "",
  companyWebsite: "",
  addressStreet: "",
  addressPostal: "",
  addressCity: "",
  phoneNumber: "",
  companyInstagramUsername: "",
  companyTikTokUsername: "",
  companyBio: "",
  selectedLearnAboutUs: null,
  learnAboutUsExtra: "",
  country: "Switzerland",
  interests: [],
  isEmailValidated: false,
  isInstagramValidated: false,
  isTikTokValidated: false,
  stepValidations: {
    credentials: false,
    company_details: false,
    interest_selection: false,
  },
  errors: {
    country: "",
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    companyName: "",
    companyWebsite: "",
    addressStreet: "",
    addressPostal: "",
    addressCity: "",
    phoneNumber: "",
    placeDescription: "",
    placeId: "",
    companyInstagramUsername: "",
    companyTikTokUsername: "",
    companyBio: "",
    selectedLearnAboutUs: "",
    learnAboutUsExtra: "",
  },
} as Pick<TRegistrationState, TNonFunctionKeys<TRegistrationState>>;

const useRegistrationStore = create<TRegistrationState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        setValue: (key, value) => {
          set(
            state => {
              state[key] = value;
            },
            false,
            "useUpgradeAccountStore/setValue",
          );

          if (key in get().errors && get().errors[key as keyof TRegistrationState["errors"]] !== "")
            get().setError(key as keyof TRegistrationState["errors"], "");
        },
        setError: (key, value) =>
          set(
            state => {
              state["errors"][key] = value;
            },
            false,
            "useUpgradeAccountStore/setError",
          ),
        selectInterest: interest => {
          const category = useCategoryStore.getState().categories.find(category => category.id === interest);

          if (!!category) {
            set(
              state => {
                if (state.interests.includes(category.id)) {
                  state.interests = state.interests.filter(i => i !== category.id);
                } else {
                  state.interests.push(category.id);
                }
              },
              false,
              "useRegistrationStore/selectInterest",
            );
          }
        },
        validateAddress: () => get().validateSchema(AddressSchema, "company_details"),
        validateSchema: (schema, step) => {
          const results = schema.safeParse(get());
          if (!results.success) {
            Object.entries(results.error.format()).forEach(([key, value]) => {
              if (value && "_errors" in value)
                get().setError(key as keyof TRegistrationState["errors"], value._errors[0]);
            });
          }
          if (schema !== EmailSchema && step)
            set(
              state => {
                state.stepValidations[step] = results.success;
              },
              false,
              "useRegistrationStore/validateSchema",
            );
          return results.success;
        },
        validateCredentials: () => {
          const isLoggedIn = !!useTokenStore.getState().userId;
          const Schema = isLoggedIn ? CredentialsSchema.omit({password: true}) : CredentialsSchema;

          return get().validateSchema(Schema, "credentials") && get().errors.email === "";
        },
        validateCompanyDetails: () => {
          set(
            state => {
              state.companyWebsite = urlify(get().companyWebsite);
            },
            false,
            "useRegistrationStore/validateCompanyDetails",
          );

          return get().validateSchema(CompanyDetailsSchema, "company_details");
        },
        validateInterestSelection: () => {
          return (
            get().validateSchema(InterestSelectionSchema, "interest_selection") &&
            get().isInstagramValidated &&
            (!get().companyTikTokUsername || get().isTikTokValidated)
          );
        },
        goToNextPage: () => {
          if (get().step === "credentials" && get().validateCredentials()) {
            set(
              state => {
                state.step = "company_details";
              },
              false,
              "useRegistrationStore/goToNextPage",
            );
          } else if (get().step === "company_details" && get().validateCompanyDetails()) {
            set(
              state => {
                state.step = "interest_selection";
              },
              false,
              "useRegistrationStore/goToNextPage",
            );
          }
        },
        register: async () => {
          const state = get();

          if (state.isRegisteringAfterDirectPurchase && !!state.userId) {
            const accessTokenFromStore = useTokenStore.getState().access;
            const accessTokenFromUrl = qs.parse(location.search, {
              ignoreQueryPrefix: true,
            }).access_token;
            const headers = {
              Authorization: `Bearer ${accessTokenFromStore ?? accessTokenFromUrl}`,
            };

            return client.patch(
              USER_KEYS.mUser(state.userId)[0],
              {
                first_name: state.firstName,
                last_name: state.lastName,
                company_name: state.companyName,
                website: state.companyWebsite,
                address_street: state.addressStreet,
                address_postal: state.addressPostal,
                address_city: state.addressCity,
                country: state.country,
                phonenumber: state.phoneNumber,
                place_description: state.placeDescription,
                place_id: state.placeId,
                username: state.companyInstagramUsername.replace("@", "").toLowerCase(),
                tiktok_username: state.companyTikTokUsername?.replace("@", "").toLowerCase(),
                interests: state.interests,
                learn_about_us: state.selectedLearnAboutUs ?? "",
                learn_about_us_name: state.learnAboutUsExtra ?? "",
                password: state.password ?? undefined,
                type: "company",
              },
              {headers},
            );
          }

          return client.post(USER_KEYS.mUserList[0], {
            email: state.email,
            first_name: state.firstName,
            last_name: state.lastName,
            password: state.password,
            company_name: state.companyName,
            website: state.companyWebsite,
            address_street: state.addressStreet,
            address_postal: state.addressPostal,
            address_city: state.addressCity,
            country: state.country,
            phonenumber: state.phoneNumber,
            place_description: state.placeDescription,
            place_id: state.placeId,
            username: state.companyInstagramUsername.replace("@", "").toLowerCase(),
            tiktok_username: state.companyTikTokUsername?.replace("@", "").toLowerCase(),
            interests: state.interests,
            learn_about_us: state.selectedLearnAboutUs ?? "",
            learn_about_us_name: state.learnAboutUsExtra ?? "",
            language_code: state.languageCode,
            type: "company",
          });
        },
        reset: () => {
          set(_ => initialState, false, "useRegistrationStore/reset");
        },
      }),
      {name: "useRegistrationStore"},
    ),
  ),
);

export default useRegistrationStore;
